import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Form, Modal, Spinner } from "react-bootstrap";
import moment from "moment";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
  FaClipboardCheck,
  FaCodeBranch,
  FaExclamationTriangle,
  FaHistory,
  FaImage,
  FaInbox,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTimesCircle,
} from "react-icons/fa";
import {
  apiSeedApproveCatalogChange,
  apiSeedGetCatalogChange,
  apiSeedListCatalogChanges,
  apiSeedRejectCatalogChange,
} from "../api/index";
import "./CatalogChangeReviewView.css";

const STATUS_FILTERS = [
  { value: "pending", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All requests" },
];

const STATUS_LABELS = {
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
};

const UGX = (value) =>
  new Intl.NumberFormat("en-UG", {
    style: "currency",
    currency: "UGX",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const shortId = (value) => {
  const id = String(value || "");
  return id ? id.slice(-10) : "Not available";
};

const displayValue = (value, formatter) => {
  if (value === null || value === undefined || value === "") return "Not set";
  return formatter ? formatter(value) : value;
};

function StatusPill({ status }) {
  return (
    <span className={`catalog-change-status is-${status || "unknown"}`}>
      <span aria-hidden="true" />
      {STATUS_LABELS[status] || status || "Unknown"}
    </span>
  );
}

function DiffRow({ label, before, after, formatter }) {
  const beforeValue = displayValue(before, formatter);
  const afterValue = displayValue(after, formatter);
  const changed = String(before ?? "") !== String(after ?? "");

  return (
    <div className={`catalog-change-diff__row${changed ? " is-changed" : ""}`}>
      <div className="catalog-change-diff__label">{label}</div>
      <div className="catalog-change-diff__value">{beforeValue}</div>
      <div className="catalog-change-diff__value">
        <span>{afterValue}</span>
        {changed && <span className="catalog-change-diff__flag">Changed</span>}
      </div>
    </div>
  );
}

function ProductImage({ src, label }) {
  return (
    <div className="catalog-change-photo">
      <span>{label}</span>
      {src ? (
        <img src={src} alt={`${label} product`} />
      ) : (
        <div className="catalog-change-photo__empty">
          <FaImage aria-hidden="true" />
          <span>No image supplied</span>
        </div>
      )}
    </div>
  );
}

function ReviewModal({ show, onHide, changeId, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(null);
  const [seededProduct, setSeededProduct] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [mode, setMode] = useState("review");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!changeId) return;
    setLoading(true);
    setError(null);
    setChange(null);
    try {
      const { data } = await apiSeedGetCatalogChange(changeId);
      setChange(data.change);
      setSeededProduct(data.seededProduct);
      setReviewNote(data.change?.reviewNote || "");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load this request.");
    } finally {
      setLoading(false);
    }
  }, [changeId]);

  useEffect(() => {
    if (show) {
      setMode("review");
      setReviewNote("");
      load();
    }
  }, [show, load]);

  const resolve = async (decision) => {
    if (decision === "reject" && !reviewNote.trim()) {
      setError("Add a reviewer note explaining why this request is rejected.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (decision === "approve") {
        await apiSeedApproveCatalogChange(changeId, {
          reviewNote: reviewNote.trim(),
        });
      } else {
        await apiSeedRejectCatalogChange(changeId, {
          reviewNote: reviewNote.trim(),
        });
      }
      onResolved?.(decision);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          `Unable to ${decision} this catalog change.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const before = seededProduct || {};
  const after = change?.proposedChanges || {};
  const isPending = change?.status === "pending";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="catalog-change-review-dialog"
    >
      <Modal.Header closeButton>
        <div className="catalog-change-modal-heading">
          <span className="catalog-change-modal-heading__icon">
            <FaClipboardCheck aria-hidden="true" />
          </span>
          <div>
            <span>Catalog governance</span>
            <Modal.Title>Review proposed change</Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="catalog-change-modal-loading">
            <Spinner animation="border" />
            <strong>Loading request details</strong>
            <span>Preparing the submitted and proposed values.</span>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="catalog-change-alert">
            <FaExclamationTriangle aria-hidden="true" />
            <span>{error}</span>
          </Alert>
        )}

        {change && !loading && (
          <>
            <div className="catalog-change-modal-meta">
              <div>
                <StatusPill status={change.status} />
                <span>
                  Submitted {moment(change.createdAt).format("DD MMM YYYY, HH:mm")}
                </span>
              </div>
              <div>
                <span>{change.targetProductRef?.kind || "Catalog product"}</span>
                <code>{shortId(change.targetProductRef?.id)}</code>
              </div>
            </div>

            <div className="catalog-change-decision-note">
              <FaHistory aria-hidden="true" />
              <p>
                Approval records the decision for the downstream catalog sync.
                It does not edit the live product immediately.
              </p>
            </div>

            <section className="catalog-change-modal-section">
              <div className="catalog-change-modal-section__heading">
                <div>
                  <span>Field comparison</span>
                  <h3>Submitted values</h3>
                </div>
                <span>Changed fields are highlighted</span>
              </div>
              <div className="catalog-change-diff">
                <div className="catalog-change-diff__header">
                  <span>Field</span>
                  <span>Seeded source</span>
                  <span>Decision record</span>
                </div>
                <DiffRow
                  label="Product name"
                  before={before.productName}
                  after={after.productName}
                />
                <DiffRow
                  label="Category"
                  before={before.category}
                  after={after.category}
                />
                <DiffRow label="Unit" before={before.unit} after={after.unit} />
                <DiffRow
                  label="Price"
                  before={before.priceUgx}
                  after={after.priceUgx}
                  formatter={UGX}
                />
              </div>
            </section>

            {(before.photoUrl || after.photoUrl) && (
              <section className="catalog-change-modal-section">
                <div className="catalog-change-modal-section__heading">
                  <div>
                    <span>Media comparison</span>
                    <h3>Product image</h3>
                  </div>
                </div>
                <div className="catalog-change-photo-grid">
                  <ProductImage src={before.photoUrl} label="Seeded source" />
                  <ProductImage src={after.photoUrl} label="Decision record" />
                </div>
              </section>
            )}

            <section className="catalog-change-modal-section">
              <Form.Group>
                <Form.Label>
                  Reviewer note
                  {mode === "reject" && <span> Required for rejection</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reviewNote}
                  onChange={(event) => setReviewNote(event.target.value)}
                  disabled={!isPending}
                  placeholder={
                    mode === "reject"
                      ? "Explain what must be corrected before resubmission."
                      : "Add optional context for the audit record."
                  }
                />
                <Form.Text>
                  {isPending
                    ? "This note is stored with the reviewer decision."
                    : change.reviewNote || "No reviewer note was recorded."}
                </Form.Text>
              </Form.Group>
            </section>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="catalog-change-button is-ghost"
          onClick={onHide}
          disabled={submitting}
        >
          Close
        </button>
        {isPending && mode === "review" && (
          <>
            <button
              type="button"
              className="catalog-change-button is-danger-outline"
              onClick={() => {
                setError(null);
                setMode("reject");
              }}
              disabled={submitting}
            >
              <FaTimesCircle aria-hidden="true" />
              Reject
            </button>
            <button
              type="button"
              className="catalog-change-button is-success"
              onClick={() => resolve("approve")}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaCheckCircle aria-hidden="true" />
              )}
              Approve request
            </button>
          </>
        )}
        {isPending && mode === "reject" && (
          <>
            <button
              type="button"
              className="catalog-change-button is-ghost"
              onClick={() => {
                setError(null);
                setMode("review");
              }}
              disabled={submitting}
            >
              Back
            </button>
            <button
              type="button"
              className="catalog-change-button is-danger"
              onClick={() => resolve("reject")}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaTimes aria-hidden="true" />
              )}
              Confirm rejection
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default function CatalogChangeReviewView() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    all: 0,
  });
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const { data } = await apiSeedListCatalogChanges({
          status: statusFilter,
          page,
          limit: 20,
        });
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
        if (data.statusCounts) setStatusCounts(data.statusCounts);
      } catch (err) {
        setError(
          err?.response?.data?.message || "Failed to load catalog changes."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter, page]
  );

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.proposedChanges?.productName,
        row.proposedChanges?.category,
        row.proposedChanges?.unit,
        row.targetProductRef?.kind,
        row.targetProductRef?.id,
        row._id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, search]);

  const startItem = total ? (page - 1) * 20 + 1 : 0;
  const endItem = total ? Math.min(page * 20, total) : 0;
  const activeFilterLabel =
    STATUS_FILTERS.find((filter) => filter.value === statusFilter)?.label ||
    "Requests";

  return (
    <div className="content catalog-change-workspace">
      <header className="catalog-change-header">
        <div className="catalog-change-header__title">
          <span className="catalog-change-header__eyebrow">
            Audit &amp; compliance
          </span>
          <h1>
            <span>
              <FaCodeBranch aria-hidden="true" />
            </span>
            Catalog change requests
          </h1>
          <p>
            Review proposed edits before they enter the vendor catalog sync
            queue.
          </p>
        </div>
        <button
          type="button"
          className="catalog-change-button is-ghost"
          onClick={() => load(true)}
          disabled={loading || refreshing}
        >
          <FaSyncAlt
            className={refreshing ? "is-spinning" : ""}
            aria-hidden="true"
          />
          {refreshing ? "Refreshing" : "Refresh"}
        </button>
      </header>

      <section className="catalog-change-metrics" aria-label="Request summary">
        <button
          type="button"
          className={`catalog-change-metric is-pending${
            statusFilter === "pending" ? " is-active" : ""
          }`}
          onClick={() => {
            setStatusFilter("pending");
            setPage(1);
          }}
        >
          <span className="catalog-change-metric__icon">
            <FaHistory aria-hidden="true" />
          </span>
          <span>
            <small>Awaiting decision</small>
            <strong>{statusCounts.pending}</strong>
          </span>
        </button>
        <button
          type="button"
          className={`catalog-change-metric is-approved${
            statusFilter === "approved" ? " is-active" : ""
          }`}
          onClick={() => {
            setStatusFilter("approved");
            setPage(1);
          }}
        >
          <span className="catalog-change-metric__icon">
            <FaCheck aria-hidden="true" />
          </span>
          <span>
            <small>Approved</small>
            <strong>{statusCounts.approved}</strong>
          </span>
        </button>
        <button
          type="button"
          className={`catalog-change-metric is-rejected${
            statusFilter === "rejected" ? " is-active" : ""
          }`}
          onClick={() => {
            setStatusFilter("rejected");
            setPage(1);
          }}
        >
          <span className="catalog-change-metric__icon">
            <FaTimes aria-hidden="true" />
          </span>
          <span>
            <small>Rejected</small>
            <strong>{statusCounts.rejected}</strong>
          </span>
        </button>
        <button
          type="button"
          className={`catalog-change-metric is-total${
            statusFilter === "all" ? " is-active" : ""
          }`}
          onClick={() => {
            setStatusFilter("all");
            setPage(1);
          }}
        >
          <span className="catalog-change-metric__icon">
            <FaClipboardCheck aria-hidden="true" />
          </span>
          <span>
            <small>Total requests</small>
            <strong>{statusCounts.all}</strong>
          </span>
        </button>
      </section>

      <section className="catalog-change-panel">
        <div className="catalog-change-panel__heading">
          <div>
            <span>Review queue</span>
            <h2>{activeFilterLabel}</h2>
          </div>
          <p>
            Decisions are retained as audit records and queued for downstream
            synchronization.
          </p>
        </div>

        <div className="catalog-change-toolbar">
          <div
            className="catalog-change-filters"
            role="group"
            aria-label="Filter catalog requests"
          >
            {STATUS_FILTERS.map((filter) => (
              <button
                type="button"
                key={filter.value}
                className={statusFilter === filter.value ? "is-active" : ""}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(1);
                }}
              >
                {filter.label}
                <span>{statusCounts[filter.value] || 0}</span>
              </button>
            ))}
          </div>
          <label className="catalog-change-search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search this page"
              aria-label="Search requests on this page"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <FaTimes aria-hidden="true" />
              </button>
            )}
          </label>
        </div>

        {error && (
          <Alert variant="danger" className="catalog-change-alert">
            <FaExclamationTriangle aria-hidden="true" />
            <span>{error}</span>
            <button type="button" onClick={() => load()}>
              Try again
            </button>
          </Alert>
        )}

        {loading ? (
          <div className="catalog-change-loading">
            <Spinner animation="border" />
            <strong>Loading review queue</strong>
            <span>Checking for the latest catalog decisions.</span>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="catalog-change-empty">
            <span className="catalog-change-empty__icon">
              {search ? (
                <FaSearch aria-hidden="true" />
              ) : (
                <FaInbox aria-hidden="true" />
              )}
            </span>
            <h3>{search ? "No matching requests" : "Queue is clear"}</h3>
            <p>
              {search
                ? "Try another product name, target type, or request ID."
                : `There are no ${activeFilterLabel.toLowerCase()} at the moment.`}
            </p>
            {search && (
              <button
                type="button"
                className="catalog-change-button is-ghost"
                onClick={() => setSearch("")}
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="catalog-change-table-wrap">
            <table className="catalog-change-table">
              <thead>
                <tr>
                  <th>Proposed product</th>
                  <th>Catalog target</th>
                  <th>Proposed price</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row._id}>
                    <td data-label="Proposed product">
                      <strong>
                        {row.proposedChanges?.productName || "Unnamed product"}
                      </strong>
                      <span>
                        {[row.proposedChanges?.category, row.proposedChanges?.unit]
                          .filter(Boolean)
                          .join(" · ") || "No category or unit supplied"}
                      </span>
                    </td>
                    <td data-label="Catalog target">
                      <span className="catalog-change-target">
                        {row.targetProductRef?.kind || "Catalog product"}
                      </span>
                      <code>{shortId(row.targetProductRef?.id)}</code>
                    </td>
                    <td data-label="Proposed price">
                      <strong>
                        {row.proposedChanges?.priceUgx !== undefined
                          ? UGX(row.proposedChanges.priceUgx)
                          : "Not supplied"}
                      </strong>
                    </td>
                    <td data-label="Submitted">
                      <strong>{moment(row.createdAt).format("DD MMM YYYY")}</strong>
                      <span>{moment(row.createdAt).fromNow()}</span>
                    </td>
                    <td data-label="Status">
                      <StatusPill status={row.status} />
                    </td>
                    <td data-label="Action">
                      <button
                        type="button"
                        className="catalog-change-review-button"
                        onClick={() => setSelectedId(row._id)}
                      >
                        {row.status === "pending" ? "Review" : "View decision"}
                        <FaArrowRight aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="catalog-change-pagination">
          <span>
            {search
              ? `${visibleRows.length} matching on this page`
              : `Showing ${startItem}-${endItem} of ${total}`}
          </span>
          <div>
            <span>
              Page {page} of {pages}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
              title="Previous page"
            >
              <FaArrowLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page >= pages || loading}
              aria-label="Next page"
              title="Next page"
            >
              <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        </footer>
      </section>

      <ReviewModal
        show={Boolean(selectedId)}
        changeId={selectedId}
        onHide={() => setSelectedId(null)}
        onResolved={() => {
          setSelectedId(null);
          load(true);
        }}
      />
    </div>
  );
}
